/**
 * ClientGoogleReviewsPage — client-wide aggregated Google reviews
 * across every GBP-linked location.
 *
 * Data source: GET /api/clients/{clientId}/gmb/reviews (added on the
 * backend alongside the per-location endpoint). Renders a distribution
 * chart, a per-location leaderboard, and the flat review feed with
 * per-location badges + star filter.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Star, Loader2, MessageSquare, ExternalLink, RefreshCw, MapPin,
} from "lucide-react";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—"
    : d.toLocaleDateString("en-AU",
      { day: "2-digit", month: "short", year: "numeric" });
};

const STAR_TO_INT = { STAR_RATING_UNSPECIFIED: 0, ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
function starInt(review) {
  if (review.star_rating === undefined) return null;
  if (typeof review.star_rating === "number") return review.star_rating;
  return STAR_TO_INT[review.star_rating] ?? null;
}

function Stars({ n }) {
  const filled = Math.max(0, Math.min(5, Number(n) || 0));
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${filled} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i}
          className={`h-3.5 w-3.5 ${i <= filled
            ? "fill-amber-500 text-amber-500"
            : "text-zinc-300"}`} />
      ))}
    </span>
  );
}

export default function ClientGoogleReviewsPage() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starFilter, setStarFilter] = useState(0);
  const [locationFilter, setLocationFilter] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (starFilter) params.star_filter = starFilter;
      if (locationFilter) params.location_id = locationFilter;
      const { data: d } = await api.get(`/clients/${clientId}/gmb/reviews`, { params });
      setData(d);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load Google reviews"));
    } finally { setLoading(false); }
  }, [clientId, starFilter, locationFilter]);

  useEffect(() => { load(); }, [load]);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post(`/clients/${clientId}/gmb/sync-all`);
      const errs = (r.errors || []).length;
      const synced = r.synced?.length || 0;
      toast.success(`Synced ${synced} location${synced === 1 ? "" : "s"}${errs ? ` · ${errs} error${errs === 1 ? "" : "s"}` : ""}`);
      await load();
    } catch (e) { toast.error(errMsg(e, "Sync failed")); }
    finally { setSyncing(false); }
  };

  const dist = data?.totals?.distribution || {};
  const totalRev = data?.totals?.reviews_total || 0;

  const maxBar = useMemo(() =>
    Math.max(1, ...Object.values(dist).map((v) => Number(v) || 0)), [dist]);

  return (
    <div className="p-6 space-y-5 max-w-[1400px]" data-testid="client-google-reviews-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Client · Google Reviews</div>
          <h1 className="font-display font-extrabold text-3xl mt-1">Google Reviews</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Aggregated feed of every Google review across all
            GBP-connected locations for this client.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={syncAll}
          disabled={syncing || !data?.totals?.locations_linked}
          data-testid="client-reviews-sync-all">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} />
          Sync all locations
        </Button>
      </header>

      {loading && !data ? (
        <Card className="rounded-sm"><CardContent className="p-8 flex items-center gap-2 text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading reviews…
        </CardContent></Card>
      ) : (data?.totals?.locations_linked ?? 0) === 0 ? (
        <Card className="rounded-sm"><CardContent className="p-8 text-sm text-zinc-500 text-center">
          <MapPin className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
          No locations are GBP-linked yet. Head to the{" "}
          <Link to={`/clients/${clientId}/locations`}
            className="text-[#002FA7] underline">
            Locations tab
          </Link>{" "}
          to connect a Google Business Profile.
        </CardContent></Card>
      ) : (
        <>
          {/* KPIs + Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="rounded-sm">
              <CardContent className="p-4">
                <div className="label-overline">Average rating</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="font-display font-extrabold text-3xl"
                    data-testid="reviews-avg-rating">
                    {(data.totals.average_rating || 0).toFixed(1)}
                  </div>
                  <Stars n={data.totals.average_rating || 0} />
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  across {totalRev.toLocaleString()} review{totalRev === 1 ? "" : "s"}
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-sm">
              <CardContent className="p-4">
                <div className="label-overline">Locations linked</div>
                <div className="font-display font-extrabold text-3xl mt-1"
                  data-testid="reviews-locations-linked">
                  {data.totals.locations_linked}
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-sm">
              <CardContent className="p-4">
                <div className="label-overline">Response rate</div>
                <div className="font-display font-extrabold text-3xl mt-1"
                  data-testid="reviews-response-rate">
                  {Math.round((data.totals.response_rate || 0) * 100)}%
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {data.totals.response_count} replies · {totalRev} total
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-sm md:col-span-1">
              <CardContent className="p-4">
                <div className="label-overline mb-2">Star distribution</div>
                <div className="space-y-1">
                  {[5, 4, 3, 2, 1].map((s) => {
                    const c = Number(dist[String(s)] || 0);
                    const pct = maxBar ? Math.round((c / maxBar) * 100) : 0;
                    return (
                      <button key={s}
                        onClick={() => setStarFilter(starFilter === s ? 0 : s)}
                        className={`w-full text-left flex items-center gap-2 hover:bg-zinc-50 rounded-sm px-1 py-0.5 transition ${starFilter === s ? "bg-amber-50" : ""}`}
                        data-testid={`reviews-star-bar-${s}`}>
                        <span className="text-[10px] font-mono w-3">{s}★</span>
                        <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400"
                            style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] font-mono w-8 text-right text-zinc-600">{c}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-zinc-400 mt-1">
                  Click a row to filter the feed
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-zinc-500">Filter:</span>
            <select value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="h-8 rounded-sm border border-zinc-200 px-2 text-xs bg-white"
              data-testid="reviews-location-filter">
              <option value="">All locations ({data.locations.length})</option>
              {data.locations.map((l) => (
                <option key={l.location_id} value={l.location_id}>{l.name}</option>
              ))}
            </select>
            {starFilter > 0 && (
              <Badge variant="outline"
                className="rounded-sm text-[10px] bg-amber-50 text-amber-800 border-amber-200 cursor-pointer"
                onClick={() => setStarFilter(0)}
                data-testid="reviews-clear-star-filter">
                {starFilter}★ · clear
              </Badge>
            )}
          </div>

          {/* Feed */}
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="h-4 w-4 text-zinc-700" />
                <h3 className="font-display font-bold text-base">
                  {data.count} review{data.count === 1 ? "" : "s"}
                </h3>
              </div>
              {loading ? (
                <div className="flex items-center gap-2 text-zinc-500 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : data.reviews.length === 0 ? (
                <div className="text-sm text-zinc-400 italic py-4 text-center">
                  No reviews match the current filters.
                </div>
              ) : (
                <div className="space-y-3">
                  {data.reviews.map((r, i) => {
                    const stars = starInt(r);
                    return (
                      <div key={r.review_id || `r-${i}`}
                        className="border border-zinc-100 rounded-sm p-3 hover:border-zinc-200 transition-colors"
                        data-testid={`review-row-${r.review_id || i}`}>
                        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">
                              {r.reviewer_display_name || "Anonymous"}
                            </span>
                            <Stars n={stars || 0} />
                            {r.location_name && (
                              <Link to={`/clients/${clientId}/locations/${r.location_id}`}
                                className="text-[10px] text-[#002FA7] hover:underline">
                                @ {r.location_name}
                              </Link>
                            )}
                          </div>
                          <span className="text-[10px] text-zinc-400 font-mono">
                            {fmtDate(r.create_time)}
                          </span>
                        </div>
                        {r.comment && (
                          <div className="text-[13px] text-zinc-700 whitespace-pre-wrap mt-1">
                            {r.comment}
                          </div>
                        )}
                        {r.reply_comment && (
                          <div className="mt-2 pl-3 border-l-2 border-emerald-200 bg-emerald-50/40 rounded-r-sm p-2">
                            <div className="text-[10px] uppercase tracking-wide text-emerald-700 mb-0.5">
                              Reply · {fmtDate(r.reply_update_time)}
                            </div>
                            <div className="text-[12px] text-zinc-700 whitespace-pre-wrap">
                              {r.reply_comment}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Per-location summary */}
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-4 w-4 text-zinc-700" />
                <h3 className="font-display font-bold text-base">Per-location summary</h3>
              </div>
              <table className="w-full text-[12px]" data-testid="reviews-per-location">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100">
                    <th className="text-left py-1.5 pr-2">Location</th>
                    <th className="text-left py-1.5 px-2">City</th>
                    <th className="text-right py-1.5 px-2">Reviews</th>
                    <th className="text-right py-1.5 px-2">Avg</th>
                    <th className="text-left py-1.5 px-2">Last sync</th>
                    <th className="text-left py-1.5 pl-2">Live</th>
                  </tr>
                </thead>
                <tbody>
                  {data.locations.map((l) => (
                    <tr key={l.location_id}
                      className="border-b border-zinc-100 last:border-b-0"
                      data-testid={`reviews-loc-row-${l.location_id}`}>
                      <td className="py-1.5 pr-2">
                        <Link to={`/clients/${clientId}/locations/${l.location_id}`}
                          className="text-[#002FA7] hover:underline">
                          {l.name}
                        </Link>
                      </td>
                      <td className="py-1.5 px-2 text-zinc-500">
                        {[l.city, l.state].filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">{l.gmb_review_count || 0}</td>
                      <td className="py-1.5 px-2 text-right font-mono">
                        {l.gmb_average_rating ? Number(l.gmb_average_rating).toFixed(1) : "—"}
                      </td>
                      <td className="py-1.5 px-2 text-[10px] text-zinc-500 font-mono">
                        {l.gmb_last_synced_at
                          ? String(l.gmb_last_synced_at).slice(0, 16).replace("T", " ")
                          : "—"}
                      </td>
                      <td className="py-1.5 pl-2">
                        {l.gmb_review_url && (
                          <a href={l.gmb_review_url} target="_blank" rel="noreferrer"
                            className="text-[10px] text-[#002FA7] hover:underline inline-flex items-center gap-0.5">
                            view <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
